import type { DataReportsPayload } from "../../../api/dataReports";

export type MemberGrowthRow = { month: string; new: number; total: number; active: number };

function normalizeAge(rows: unknown): { label: string; value: number; pct: number }[] {
  if (!Array.isArray(rows)) return [];
  const out: { label: string; value: number; pct: number }[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const label = String(o.label ?? "");
    if (!label) continue;
    out.push({
      label,
      value: Number(o.value ?? 0),
      pct: Number(o.pct ?? 0),
    });
  }
  return out;
}

export function buildDataReportsDerived(p: DataReportsPayload | null) {
  if (!p) {
    return {
      assocName: "",
      generatedAtLabel: "",
      months: 6,
      memberData: [] as MemberGrowthRow[],
      maxNewMembers: 1,
      maxActive: 1,
      activityData: [] as DataReportsPayload["activityData"],
      activityAnalysis: [] as DataReportsPayload["activityAnalysis"],
      activityTypeStats: [] as DataReportsPayload["activityTypeStats"],
      memberGenderData: [] as DataReportsPayload["genderDistribution"],
      memberAgeData: [] as { label: string; value: number; pct: number }[],
      memberLevelData: [] as DataReportsPayload["memberLevelData"],
      memberLevelTotal: 0,
      keyMetrics: [] as DataReportsPayload["keyMetrics"],
      pointsMonthlyData: [] as { month: string; issued: number; consumed: number }[],
      pointsByLevelData: {} as Record<string, { month: string; issued: number; consumed: number }[]>,
      levelOptions: ["全部"] as string[],
      topRedeemProducts: [] as DataReportsPayload["topRedeemProducts"],
      redeemSummary: null as DataReportsPayload["redeemSummary"] | null,
      pointsSourceDistribution: [] as DataReportsPayload["pointsSourceDistribution"],
      levelPointsOverview: [] as DataReportsPayload["levelPointsOverview"],
      coreMetrics: null as DataReportsPayload["coreMetrics"] | null,
      activitySummary: null as DataReportsPayload["activitySummary"] | null,
      activityHighlights: [] as DataReportsPayload["activityHighlights"],
    };
  }

  const memberData: MemberGrowthRow[] = p.memberGrowth.map((d) => ({
    month: d.month,
    new: d.newCount,
    total: d.total,
    active: d.activeCount,
  }));

  const maxNewMembers = Math.max(p.maxNew, 1, ...memberData.map((d) => d.new));
  const maxActive = Math.max(p.maxActive, 1, ...memberData.map((d) => d.active));
  const memberLevelTotal =
    p.memberLevelTotal > 0 ? p.memberLevelTotal : p.memberLevelData.reduce((s, x) => s + x.value, 0);

  const pointsMonthlyData = p.pointsMonthly.map((d) => ({
    month: d.month,
    issued: d.issued,
    consumed: d.consumed,
  }));

  const pointsByLevelData: Record<string, { month: string; issued: number; consumed: number }[]> = {
    全部: pointsMonthlyData,
    ...p.pointsMonthlyByLevel,
  };

  const levelOptions = ["全部", ...p.pointsLevelNames];

  const generatedAtLabel =
    p.generatedAt?.trim() ||
    new Date().toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric" });

  return {
    assocName: p.assocName ?? "",
    generatedAtLabel,
    months: p.months,
    memberData,
    maxNewMembers,
    maxActive,
    activityData: p.activityData,
    activityAnalysis: p.activityAnalysis,
    activityTypeStats: p.activityTypeStats,
    memberGenderData: p.genderDistribution,
    memberAgeData: normalizeAge(p.ageDistribution),
    memberLevelData: p.memberLevelData,
    memberLevelTotal: Math.max(memberLevelTotal, 1),
    keyMetrics: p.keyMetrics,
    pointsMonthlyData,
    pointsByLevelData,
    levelOptions,
    topRedeemProducts: p.topRedeemProducts,
    redeemSummary: p.redeemSummary,
    pointsSourceDistribution: p.pointsSourceDistribution,
    levelPointsOverview: p.levelPointsOverview,
    coreMetrics: p.coreMetrics,
    activitySummary: p.activitySummary,
    activityHighlights: p.activityHighlights,
  };
}
